import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { isValid } from 'date-fns';

import AtendenteSelectField from 'components/AtendenteSelectField';
import AutocompleteAtendimentoReason from 'components/AutocompleteAtendimentoReason';
import ContatoSelectField from 'components/ContatoSelectField';
import DebouncedInput from 'components/DebouncedInput';
import DialogHeader from 'components/DialogHeader';
import FilterTagsV2 from 'components/TagsV2/FilterTagsV2';
import ChannelSelectField from 'pages/Contatos/components/ChannelSelectField';
import RangeDateFilter from 'pages/Contatos/components/RangeDateFilter';
import { useAppDispatch } from 'store';
import {
  AtendimentoReasonInterface,
  FiltersHistoricoParams,
} from 'store/reducers/atendimentos';
import {
  fetchCanaisSelect,
  SelectChannelInterface,
} from 'store/reducers/canais';
import { ContatoSelectInterface } from 'store/reducers/contatos';
import { TagInterface } from 'store/reducers/tags';
import { UsuarioInterfaceV2 } from 'store/reducers/usuarios';
import { IFiltersOptions } from 'types';

export type ModalFilterOptionsType = Omit<
  FiltersHistoricoParams,
  | 'dateFinishedStart'
  | 'dateFinishedEnd'
  | 'dateCreatedAtStart'
  | 'dateCreatedAtEnd'
> & {
  rangeDate: MaterialUiPickersDate[];
  rangeDateCreatedAt: MaterialUiPickersDate[];
  selectedTags: TagInterface[];
  contato?: ContatoSelectInterface;
  atendente?: UsuarioInterfaceV2;
  canal?: SelectChannelInterface;
  reasonId?: string;
  reason?: AtendimentoReasonInterface;
};

export const defaultModalFilters: ModalFilterOptionsType = {
  rangeDate: [],
  rangeDateCreatedAt: [],
  participantId: undefined,
  tagIds: [],
  selectedTags: [],
  channelCodes: [],
  message: '',
  contato: undefined,
  code: undefined,
  atendente: undefined,
  reasonId: undefined,
  reason: undefined,
};

export function mapModalFiltersToChips(
  options: ModalFilterOptionsType,
): IFiltersOptions[] {
  const results: IFiltersOptions[] = [];

  if (
    options.rangeDateCreatedAt?.length === 2 &&
    options.rangeDateCreatedAt[0] &&
    isValid(options.rangeDateCreatedAt[0])
  ) {
    const start = options.rangeDateCreatedAt[0];
    const end = options.rangeDateCreatedAt[1];
    results.push({
      property: 'rangeDateCreatedAt',
      value: options.rangeDateCreatedAt,
      labelProperty: 'Iniciado em',
      labelValue: `${start.toLocaleDateString()} - ${
        end && isValid(end) ? end.toLocaleDateString() : ''
      }`,
    });
  }

  if (options.selectedTags?.length > 0) {
    results.push({
      property: 'tags',
      value: options.tagIds || [],
      labelProperty: 'Tags',
      labelValue: options.selectedTags.map(t => t.nome).join(', '),
    });
  }

  if (options.contato) {
    results.push({
      property: 'contatoId',
      value: options.contato.id,
      labelProperty: 'Contato',
      labelValue: options.contato.name,
    });
  }

  if (options.code) {
    results.push({
      property: 'code',
      value: options.code,
      labelProperty: 'Código',
      labelValue: options.code,
    });
  }

  if (
    options.rangeDate?.length === 2 &&
    options.rangeDate[0] &&
    isValid(options.rangeDate[0])
  ) {
    const start = options.rangeDate[0];
    const end = options.rangeDate[1];
    results.push({
      property: 'rangeDate',
      value: options.rangeDate,
      labelProperty: 'Finalizado em',
      labelValue: `${start.toLocaleDateString()} - ${
        end && isValid(end) ? end.toLocaleDateString() : ''
      }`,
    });
  }

  if (options.participantId) {
    results.push({
      property: 'participantId',
      value: options.participantId,
      labelProperty: 'Participação',
      labelValue: options.atendente?.name || '',
    });
  }

  if (options.channelCodes && options.channelCodes.length > 0) {
    results.push({
      property: 'channelCodes',
      value: options.channelCodes,
      labelProperty: 'Canal',
      labelValue: options.canal?.name || '',
    });
  }
  if (options.reason) {
    results.push({
      property: 'reasonId',
      value: options.reason.id,
      labelProperty: 'Motivo do atendimento',
      labelValue: options.reason.name,
    });
  }

  return results;
}

type ModalFiltersProps = {
  open: boolean;
  onClose: () => void;
  initialFilters: ModalFilterOptionsType;
  onApply: (filters: ModalFilterOptionsType, chips: IFiltersOptions[]) => void;
  onClear: () => void;
  isFinalizado?: boolean;
};

export default function ModalFilters(props: ModalFiltersProps) {
  const { open, onClose, initialFilters, onApply, onClear, isFinalizado } =
    props;
  const dispatch = useAppDispatch();

  const [localFilters, setLocalFilters] =
    useState<ModalFilterOptionsType>(initialFilters);

  // Sincroniza quando o modal abre com os filtros aplicados atualmente
  useEffect(() => {
    if (open) {
      setLocalFilters(initialFilters);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(fetchCanaisSelect());
  }, [dispatch]);

  const hasAnyFilter = (filters: ModalFilterOptionsType): boolean => {
    return (
      !!filters.contato ||
      !!filters.code ||
      !!filters.atendente ||
      !!filters.canal ||
      (filters.rangeDate?.length === 2 && !!filters.rangeDate[0]) ||
      (filters.selectedTags?.length || 0) > 0 ||
      !!filters.participantId ||
      (filters.channelCodes?.length || 0) > 0 ||
      (filters.rangeDateCreatedAt?.length === 2 &&
        !!filters.rangeDateCreatedAt[0]) ||
      !!filters.reason
    );
  };

  const handleChange = (newFilters: Partial<ModalFilterOptionsType>) => {
    setLocalFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleApply = () => {
    if (!hasAnyFilter(localFilters)) return;

    onApply(localFilters, mapModalFiltersToChips(localFilters));
    onClose();
  };

  const handleClear = () => {
    if (!hasAnyFilter(localFilters)) {
      onClose();
      return;
    }

    onClear();
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="xs" open={open}>
      <DialogHeader title="Filtrar atendimentos" onClose={onClose} />

      <DialogContent>
        <Box display="flex" flexDirection="column" gridGap={16} paddingTop={1}>
          <ContatoSelectField
            contatoId={localFilters.contato?.id || ''}
            onFilter={(contato: ContatoSelectInterface | null) =>
              handleChange({
                contato: contato || undefined,
                contatoId: contato?.id || undefined,
              })
            }
            onReset={() =>
              handleChange({ contato: undefined, contatoId: undefined })
            }
          />

          <DebouncedInput
            value={localFilters.code || ''}
            onChange={value => handleChange({ code: value || undefined })}
            placeholder="Código"
            onClear={() => handleChange({ code: undefined })}
          />

          <AtendenteSelectField
            label="Participação"
            atendenteId={
              localFilters.atendente?.id || localFilters.participantId || ''
            }
            onFilter={atendente =>
              handleChange({
                atendente: atendente || undefined,
                participantId: atendente?.id || undefined,
              })
            }
            onReset={() =>
              handleChange({ atendente: undefined, participantId: undefined })
            }
          />

          <RangeDateFilter
            label="Iniciado em"
            rangeDate={localFilters.rangeDateCreatedAt}
            onFilterRangeDate={(rangeDates: MaterialUiPickersDate[]) =>
              handleChange({ rangeDateCreatedAt: rangeDates })
            }
            onResetRangeDate={() => handleChange({ rangeDateCreatedAt: [] })}
          />

          {isFinalizado && (
            <RangeDateFilter
              label="Finalizado em"
              rangeDate={localFilters.rangeDate}
              onFilterRangeDate={(rangeDates: MaterialUiPickersDate[]) =>
                handleChange({ rangeDate: rangeDates })
              }
              onResetRangeDate={() => handleChange({ rangeDate: [] })}
            />
          )}

          <ChannelSelectField
            value={localFilters.channelCodes?.[0] || ''}
            onFilter={(canal: SelectChannelInterface | null) => {
              handleChange({
                channelCodes: canal ? [canal.code] : [],
                canal: canal || undefined,
              });
            }}
            onReset={() => handleChange({ channelCodes: [], canal: undefined })}
          />

          {isFinalizado && (
            <AutocompleteAtendimentoReason
              value={localFilters.reason || null}
              onChange={(_, value) =>
                handleChange({
                  reason: value || undefined,
                  reasonId: value?.id || undefined,
                })
              }
            />
          )}

          <FilterTagsV2
            handleFilter={(tags: TagInterface[]) =>
              handleChange({
                selectedTags: tags,
                tagIds: tags.map(t => t.id).filter(Boolean) as string[],
              })
            }
            values={localFilters.selectedTags || []}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClear}>Limpar</Button>
        <Button variant="contained" color="primary" onClick={handleApply}>
          Aplicar filtros
        </Button>
      </DialogActions>
    </Dialog>
  );
}
